import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraAssistProposalCard, SpecraButton, SpecraInlineBanner, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { assistWriteHeaders } from '../../../lib/assistAttribution';
import { projectDataClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
function str(params, key) {
    const v = params[key];
    return typeof v === 'string' ? v : '';
}
/**
 * Apply an AI-proposed record change via the projectdata RPC. The call goes
 * through sabaody under the *human's* JWT principal — so the AI never writes
 * directly, and the resulting change is attributed to the approving user
 * (R03; FND-27 AWR-3). The Assist headers label the write, never authorize it.
 */ async function applyRecordWrite(action, params, opts) {
    switch(action){
        case 'update-deliverable-status':
            await projectDataClient.updateDeliverableStatus({
                projectId: str(params, 'projectId'),
                deliverableId: str(params, 'deliverableId'),
                status: str(params, 'status'),
                notes: str(params, 'notes')
            }, opts);
            return;
        case 'update-clin-status':
            await projectDataClient.updateCLINStatus({
                projectId: str(params, 'projectId'),
                clinId: str(params, 'clinId'),
                fundingStatus: str(params, 'fundingStatus'),
                notes: str(params, 'notes')
            }, opts);
            return;
        case 'update-requirement-status':
            {
                const mapping = params.mapping;
                await projectDataClient.updateRequirementStatus({
                    projectId: str(params, 'projectId'),
                    requirementId: str(params, 'requirementId'),
                    status: str(params, 'status'),
                    notes: str(params, 'notes'),
                    ...mapping && typeof mapping === 'object' ? {
                        mapping: {
                            proposalSection: str(mapping, 'proposalSection'),
                            responseText: str(mapping, 'responseText'),
                            pageReference: str(mapping, 'pageReference')
                        }
                    } : {}
                }, opts);
                return;
            }
        case 'create-contract-from-proposal':
            {
                const contractNumber = str(params, 'contractNumber');
                const contractType = str(params, 'contractType');
                await projectDataClient.createContractProjectFromProposal({
                    proposalProjectId: str(params, 'proposalProjectId'),
                    name: str(params, 'name'),
                    description: str(params, 'description'),
                    archiveSourceProposal: params.archiveSourceProposal === true,
                    ...contractNumber || contractType ? {
                        contractData: {
                            contractNumber,
                            contractType
                        }
                    } : {}
                }, opts);
                return;
            }
        default:
            throw new Error(`Unknown record-write action: ${action}`);
    }
}
const ACTION_LABELS = {
    'update-deliverable-status': 'Update deliverable status',
    'update-clin-status': 'Update CLIN funding status',
    'update-requirement-status': 'Update requirement status',
    'create-contract-from-proposal': 'Convert proposal → contract'
};
/**
 * Renders an AI-proposed record/status change on SpecraAssistProposalCard
 * (FND-27 AWR-2): each proposed value, Accept / Reject, and a comment back to
 * the assistant. Accept applies it via a projectdata RPC as the user.
 */ export function RecordWriteCard({ output, toolCallId }) {
    const [state, setState] = useState('pending');
    const [execError, setExecError] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [showComment, setShowComment] = useState(false);
    const sendFeedback = useChatFeedback();
    const { user } = useAuth();
    if (!output.success || !output.action || !output.params) {
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            tone: "danger",
            variant: "framed",
            children: output.error || 'Failed to generate the proposed change.'
        });
    }
    const action = output.action;
    const params = output.params;
    const label = ACTION_LABELS[action] || action;
    const headers = output.approvalConversationId && toolCallId ? assistWriteHeaders(output.approvalConversationId, toolCallId) : {
        'X-Specra-Via': 'assist'
    };
    const handleAccept = async ()=>{
        setState('applying');
        setExecError(null);
        try {
            await applyRecordWrite(action, params, {
                headers
            });
        } catch (err) {
            setExecError(err instanceof Error ? err.message : 'Failed to apply the change');
            setState('pending');
            return;
        }
        setState('accepted');
        sendFeedback(`User approved and applied the proposed change: ${output.summary || label}. The change was written under the user's account.`);
    };
    const handleReject = ()=>{
        setState('rejected');
        sendFeedback(`User rejected the proposed change: ${output.summary || label}. Ask what they would like to do instead.`);
    };
    const handleComment = ()=>{
        if (!commentText.trim()) return;
        setState('commented');
        sendFeedback(`User feedback on the proposed change: ${commentText.trim()}`);
        setShowComment(false);
    };
    const items = Object.entries(params).filter(([, v])=>v !== '' && v !== undefined && v !== null && typeof v !== 'object').map(([k, v])=>({
            id: k,
            label: k,
            after: String(v)
        }));
    // "commented" keeps the proposal open on the server side; the card shows it
    // as decided-for-now so the user is not asked twice.
    const cardStatus = state === 'commented' ? 'stale' : state === 'applying' ? 'applying' : state;
    const approver = user?.name || user?.email || 'You';
    return /*#__PURE__*/ _jsx(SpecraAssistProposalCard, {
        acceptLabel: "Accept & apply",
        actionTestIds: {
            accept: Testids.assistant.recordWriteAccept,
            reject: Testids.assistant.recordWriteReject
        },
        "data-testid": Testids.assistant.recordWriteCard,
        decidedBy: state === 'accepted' || state === 'rejected' ? approver : undefined,
        error: execError,
        items: items,
        kind: "record",
        onAccept: handleAccept,
        onReject: handleReject,
        preview: state === 'pending' ? showComment ? /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-2)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "aria-label": "Comment for the assistant",
                    onChange: (e)=>setCommentText(e.target.value),
                    placeholder: "Tell the AI what to change...",
                    rows: 2,
                    value: commentText
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>setShowComment(false),
                            size: "sm",
                            variant: "ghost",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: !commentText.trim(),
                            onClick: handleComment,
                            size: "sm",
                            variant: "secondary",
                            children: "Send Feedback"
                        })
                    ]
                })
            ]
        }) : /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>setShowComment(true),
                size: "sm",
                variant: "ghost",
                children: "Comment"
            })
        }) : state === 'commented' ? /*#__PURE__*/ _jsx(SpecraText, {
            size: "caption",
            tone: "secondary",
            children: "Feedback sent — waiting for AI"
        }) : null,
        status: cardStatus,
        summary: output.summary,
        target: label,
        title: "Proposed change — approval required"
    });
}
